import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Input from "../input/Input";
import { useAppDispatch } from "../../app/hooks";
import { verifySignInCodeAsync } from "../../features/authentication/authenticationApi";
import { resolvePathAfterSignIn } from "../../features/contexts/utils/postSignIn";

interface TrialCodeStepProps {
  email: string;
  onChangeEmail: () => void;
}

/**
 * Second step of the email-only trial start: the account exists and the welcome
 * email is on its way. Clicking the link in it signs the visitor in on its own;
 * typing the 6-digit code here does the same without leaving this tab — which is
 * what makes the flow work inside in-app browsers that block OAuth pop-ups.
 */
const TrialCodeStep = ({ email, onChangeEmail }: TrialCodeStepProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();
    setCodeError(null);

    if (code.trim().length < 6) {
      setCodeError(t("auth.passwordless.codeRequired"));
      return;
    }

    setLoading(true);
    const result = await dispatch(verifySignInCodeAsync({ email, code: code.trim() }));
    setLoading(false);

    if (verifySignInCodeAsync.fulfilled.match(result)) {
      navigate(await resolvePathAfterSignIn(dispatch));
    } else {
      setCodeError(t("auth.passwordless.codeInvalid"));
    }
  };

  return (
    <form onSubmit={handleVerify} noValidate>
      <h2 className="text-grey-20 dark:text-white">{t("auth.trial.sentTitle")}</h2>
      <p className="mt-2 text-grey-50 dark:text-grey-70">{t("auth.trial.sentSubtitle", { email })}</p>

      <div className="mt-6">
        <Input
          label={t("auth.passwordless.codeLabel")}
          type="text"
          name="trialCode"
          id="trialCode"
          autoComplete="one-time-code"
          fullWidth
          margin="dense"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            if (codeError) setCodeError(null);
          }}
          disabled={loading}
          error={!!codeError}
          helperText={codeError}
          labelPosition="top-left"
        />
      </div>

      <button
        type="submit"
        className="btn btn-primary w-full h-[46px] mt-2 flex items-center justify-center text-[16px] no-underline"
        disabled={loading}
      >
        {t("auth.trial.continueWithCode")}
      </button>

      <p className="mt-4 text-center text-xs text-grey-50 dark:text-grey-60">{t("auth.trial.sentExpiry")}</p>

      <div className="mt-4 text-center">
        <button
          type="button"
          className="text-sm cursor-pointer hover:underline text-primary-40 dark:text-primary-90 bg-transparent border-none"
          onClick={onChangeEmail}
        >
          {t("auth.passwordless.useAnotherEmail")}
        </button>
      </div>
    </form>
  );
};

export default TrialCodeStep;
